import {gql} from '@apollo/client';
import {noop} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExcludeInaccessible from 'graphql-client/ExcludeInaccessible';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import notifications from 'notifications';
import * as Colors from 'ui-library/Colors';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import AddIcon from 'ui-library/icons/AddIcon';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import runModal from 'ui-library/runModal';
import {Cell, Column, Table} from 'ui-library/simple-table/SimpleTable';
import {withLoadingScreen} from 'util/Promises';
import ValidationRulesModal from 'validation_rules/modals/ValidationRulesModal';
import {CreateValidationRulesParams, CreateValidationRulesResponse} from 'validation_rules/network/types';

import {ValidationRulesQuery, ValidationRulesQueryVariables} from './__generated/ValidationRulesQuery';

export type ValidationRule = ExcludeInaccessible<
  ValidationRulesQuery['hasValidationRulesItem']
>['validationRules'][number];

export const VALIDATION_RULES_QUERY = gql`
  query ValidationRulesQuery($id: StrictString!) @skipAudit @engComponent(name: VALIDATION_RULES_ENGINE) {
    hasValidationRulesItem(id: $id) {
      ... on HasValidationRulesInterface {
        id
        validationRules {
          ... on ValidationRuleNode {
            id
            name
            errorMessage
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

interface ValidationRulesTableProps {
  validationRules: Array<ValidationRule>;
  hasValidationRulesItemId: string;
  onCreateValidationRules: (
    createParams: CreateValidationRulesParams
  ) => Promise<CreateValidationRulesResponse>;
  refetchValidationRules: () => void;
}

export function ValidationRulesTable(props: ValidationRulesTableProps): JSX.Element {
  async function createValidationRules(createParams: CreateValidationRulesParams): Promise<void> {
    await withLoadingScreen(props.onCreateValidationRules(createParams), 'Creating rule...');
    props.refetchValidationRules();
  }

  return (
    <Table
      numRows={props.validationRules.length}
      emptyTableText="This schema does not have any validation rules."
      getRowClassName={() => css(styles.tableRow)}
    >
      <Column
        header={
          <Cell>
            Rules
            <AddIcon
              onClick={() => {
                runModal(ValidationRulesModal, {
                  hasValidationRulesItemId: props.hasValidationRulesItemId,
                  onRequestClose: noop,
                  onRequestSubmit: createValidationRules,
                });
              }}
            />
          </Cell>
        }
        renderCell={(rowIndex: number) => props.validationRules[rowIndex].name}
      />
      <Column
        header="Error Message"
        renderCell={(rowIndex: number) => props.validationRules[rowIndex].errorMessage}
      />

      <Column
        renderCell={(rowIndex: number) => (
          <Cell className="u-text-align-right">
            <div className="u-display-inline-block">
              <BenchlingIcon
                className={classNames('updateIcon', css(styles.updateIcon))}
                onClick={noop}
                icon="edit"
              />
              <BenchlingIcon
                className={classNames('deleteIcon', 'u-spacing-left', css(styles.deleteIcon))}
                onClick={noop}
                icon="delete"
              />
            </div>
          </Cell>
        )}
      />
    </Table>
  );
}

type QueryingValidationRulesTableProps = Omit<
  ValidationRulesTableProps,
  'validationRules' | 'refetchValidationRules'
>;

export default function QueryingValidationRulesTable(
  props: QueryingValidationRulesTableProps
): JSX.Element | null {
  const result = useBenchlingQuery<ValidationRulesQuery, ValidationRulesQueryVariables>(
    VALIDATION_RULES_QUERY,
    {
      variables: {id: props.hasValidationRulesItemId},
    }
  );
  if (result.type === QueryResultType.PENDING) {
    return <HorizontalThrobber />;
  } else if (result.type === QueryResultType.ERROR) {
    notifications.error('Error loading validation rules. Try refreshing the page.');
    return null;
  } else if (isInaccessible(result.data?.hasValidationRulesItem)) {
    return <div>This item is inaccessible.</div>;
  } else {
    const validationRules = isInaccessible(result.data?.hasValidationRulesItem)
      ? []
      : result.data?.hasValidationRulesItem.validationRules;
    return (
      <ValidationRulesTable
        {...props}
        validationRules={validationRules}
        refetchValidationRules={result.refetch}
      />
    );
  }
}

const styles = StyleSheet.create({
  tableRow: {
    ':hover .deleteIcon': {
      visibility: 'visible',
    },
    ':hover .updateIcon': {
      visibility: 'visible',
    },
  },
  updateIcon: {
    color: Colors.GRAY_DARK,
    visibility: 'hidden',
    cursor: 'pointer',
    ':hover': {
      color: Colors.BLUE_LIGHT,
      visibility: 'visible',
    },
  },
  deleteIcon: {
    color: Colors.GRAY_DARK,
    visibility: 'hidden',
    cursor: 'pointer',
    ':hover': {
      color: Colors.RED_LIGHT,
      visibility: 'visible',
    },
  },
});
